import { useEffect } from "react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";

/**
 * Workshop: ConfirmDialog — centered modal confirmation dialog.
 *
 * Semi-transparent backdrop + glass card with title, message, and
 * Cancel / Confirm buttons. Destructive variant turns the confirm button
 * red. Escape key fires onCancel.
 */

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  "data-testid"?: string;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  destructive = false,
  onConfirm,
  onCancel,
  "data-testid": testId = "confirm-dialog",
}: ConfirmDialogProps) {
  // Escape key handler
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCancel();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      data-testid={testId}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
        data-testid={`${testId}-backdrop`}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className={cn(
          "relative z-10 w-full max-w-sm rounded-xl",
          "border border-glass-stroke bg-glass-fill-dense shadow-glass backdrop-blur-md",
          "p-5",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        aria-describedby={`${testId}-message`}
        data-testid={`${testId}-card`}
      >
        {/* Title */}
        <h2
          id={`${testId}-title`}
          className="mb-2 text-[13px] font-semibold text-primary"
        >
          {title}
        </h2>

        {/* Message */}
        <p
          id={`${testId}-message`}
          className="mb-5 text-[11px] leading-relaxed text-secondary"
        >
          {message}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            size="sm"
            onClick={onConfirm}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export type { ConfirmDialogProps };
